import { Button } from 'blaze-ui/Button'
import Styles from './BrandVisualStyleMedia.module.scss'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useDeleteMediaFile } from 'hooks/queries/mediaFiles/useDeleteMediaFile'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useEffect, useState } from 'react'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { RecommendationBanner } from './RecommendationBanner'
import { MediaFilesGrid } from './MediaFilesGrid'
import { UploadMoreButton } from './UploadMoreButton'
import cx from 'classnames'
import { GoogleDriveImport } from './GoogleDriveImport'

export interface BrandVisualStyleMediaProps {
  onNext: (optIn: boolean) => void
  onBack: () => void
  brandKitId: number
  forVisualStyles?: boolean
}

export const BrandVisualStyleMedia = ({
  onNext,
  onBack,
  brandKitId,
  forVisualStyles = false,
}: BrandVisualStyleMediaProps) => {
  const { data: brandKitData, isLoading, isFetched } = useBrandKit(brandKitId, { includeAllMediaFiles: true })
  const [showAllMediaFiles, setShowAllMediaFiles] = useState(false)
  const [useStockMedia, setUseStockMedia] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const { mutateAsync: updateBrandKitMutation } = useUpdateBrandKit({ id: brandKitId })
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const preferences = brandKitData?.brand_kit?.content_preferences

  useEffect(() => {
    if (!preferences) return

    if (!('image_media_priority' in preferences)) {
      setUseStockMedia(true)
    } else {
      setUseStockMedia(preferences.image_media_priority === 'brand_kit_first')
    }
  }, [preferences])

  const handleOptIn = () => {
    updateBrandKitMutation(
      {
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          apply_visual_style: true,
        },
      },
      {
        onSuccess: () => onNext(true),
      },
    )
  }

  const handleUseStockMedia = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked
    setUseStockMedia(value)
  }

  const { mutate: deleteMediaFile } = useDeleteMediaFile({
    onSuccess: () => {
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      queryClient.invalidateQueries({
        queryKey: ['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles', 'list'],
        exact: false,
      })
    },
    onError: () => {
      showErrorMessageToast('Could not delete media file.')
    },
  })

  const brandKitMediaFiles = brandKitData?.brand_kit.media_files || []
  const images = brandKitMediaFiles.filter((mediaFile) => mediaFile.mime_type?.startsWith('image'))
  const videos = brandKitMediaFiles.filter((mediaFile) => mediaFile.mime_type?.startsWith('video'))

  const enoughMedia = brandKitMediaFiles.length >= 30
  const isLessThanEnough = brandKitMediaFiles.length < 30 && brandKitMediaFiles.length > 0
  const noMedia = brandKitMediaFiles.length === 0

  const handleNext = () => {
    updateBrandKitMutation(
      {
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          image_media_priority: useStockMedia ? 'brand_kit_first' : 'only_my_brand_kit',
          video_media_priority: useStockMedia ? 'brand_kit_first' : 'only_my_brand_kit',
        },
      },
      {
        onSuccess: () => {
          setUseStockMedia(!useStockMedia)
          queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
        },
      },
    )
    onNext(false)
  }

  if (forVisualStyles) {
    return (
      <MultiStepStepLayout centerContent scrollContainerClassName={Styles.scrollContainer}>
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <div className={Styles.footerSide}>
                  <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                    Back
                  </Button>
                </div>
                <div className={Styles.footerSide}>
                  <Button
                    size="lg"
                    variant={noMedia ? 'secondary' : 'primary'}
                    onPress={() => onNext(true)}
                    aria-label="Continue"
                    isDisabled={isUploading}
                  >
                    {noMedia ? 'Maybe Later' : 'Select a Brand Font'}
                  </Button>
                </div>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={cx(Styles.container, Styles.forVisualStyles)}>
            {noMedia && (
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1766521526/blaze_assets/virtual_marketer/onboarding/Frame_1171276974_a8fjdc.png"
                alt="empty"
                className={Styles.emptyImage}
              />
            )}
            <div className={Styles.headingContainer}>
              <Heading level={1}>Add photos and videos to transform with your new style</Heading>
            </div>
            {brandKitMediaFiles.length > 0 && (
              <MediaFilesGrid
                mediaFiles={brandKitMediaFiles}
                showAllMediaFiles={showAllMediaFiles}
                onShowAllMediaFiles={setShowAllMediaFiles}
                onDelete={(id) => deleteMediaFile(id as number)}
                brandKitId={brandKitId}
              />
            )}
            <UploadMoreButton
              brandKitId={brandKitId}
              onIsUploadingChange={setIsUploading}
              variant={noMedia ? 'primary' : 'secondary'}
            />
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  let title: string | React.ReactNode = 'Add photos and videos to transform with your new style'
  let description = ''

  if (enoughMedia) {
    title = (
      <span>
        We have {<span className={Styles.bold}>{images.length || 'No'}</span>} images
        {!!videos.length && <>and {<span className={Styles.bold}>{videos.length || 'No'}</span>} videos</>} — enough
        content for roughly two months of posting.
      </span>
    )
    description = ''
  }

  if (isLessThanEnough) {
    title = (
      <span>
        We have {<span className={Styles.bold}>{images.length || 'No'}</span>} images and{' '}
        {<span className={Styles.bold}>{videos.length || 'No'}</span>} videos — That's not enough content for your
        marketing needs.
      </span>
    )
    description = ''
  }

  if (noMedia && !isLoading && isFetched) {
    title = "We don't have any images to use for your content."
    description = ''
  }

  const footer = (
    <StickyFooter>
      <FooterButtons>
        <div className={Styles.footerSide}>
          <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
            Back
          </Button>
        </div>
        <div className={Styles.footerSide}>
          <Button
            size="lg"
            variant="secondary"
            onPress={handleNext}
            aria-label="Setup Your Channels"
            isDisabled={isUploading}
          >
            Continue to Setup Channels
          </Button>
        </div>
      </FooterButtons>
    </StickyFooter>
  )

  if (isLoading || !isFetched) {
    return (
      <MultiStepStepLayout
        title={title}
        description={description}
        centerContent
        scrollContainerClassName={Styles.scrollContainer}
      >
        <StickyFooterProvider footer={footer}>
          <div className={Styles.container}>
            <div className={Styles.mediaGrid}>
              {Array.from({ length: 10 }).map((_, index) => (
                <div className={Styles.mediaItem} key={`loading-${index}`}>
                  <ImageThumbnail forceLoading image={{ url: '' }} />
                </div>
              ))}
            </div>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout
      title={title}
      description={description}
      centerContent
      scrollContainerClassName={Styles.scrollContainer}
    >
      <StickyFooterProvider footer={footer}>
        <div className={Styles.container}>
          {isLessThanEnough && <RecommendationBanner onPress={handleOptIn} />}
          {brandKitMediaFiles.length > 0 && (
            <MediaFilesGrid
              mediaFiles={brandKitMediaFiles}
              showAllMediaFiles={showAllMediaFiles}
              onShowAllMediaFiles={setShowAllMediaFiles}
              onDelete={(id) => deleteMediaFile(id as number)}
              brandKitId={brandKitId}
            />
          )}
          {enoughMedia && (
            <div className={Styles.useStockMediaContainer}>
              <ToggleButton checked={useStockMedia} name="use-stock-media" handleChange={handleUseStockMedia} />
              <Text>Use stock photography and videos when uploads run out</Text>
            </div>
          )}
          {noMedia && (
            <div className={Styles.noMediaFiles}>
              <RecommendationBanner onPress={handleOptIn} />
              <div className={Styles.uploadMoreContainer}>
                <Heading level={4}>
                  If you have large quantity of great photos and videos you can upload them below.
                </Heading>

                <img
                  src="https://res.cloudinary.com/almanac/image/upload/v1766521526/blaze_assets/virtual_marketer/onboarding/Frame_1171276974_a8fjdc.png"
                  alt="empty"
                  className={Styles.optionImage}
                />
              </div>
            </div>
          )}
          <div className={Styles.importButtons}>
            <GoogleDriveImport />
            <UploadMoreButton brandKitId={brandKitId} onIsUploadingChange={setIsUploading} />
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
